import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const Success = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, setCart } = useContext(AuthContext);
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setCart([]); // Clear cart
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-20">
        <div className="bg-white rounded-3xl shadow-xl p-12 text-center" data-testid="success-page">
          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 className="h-16 w-16 animate-spin text-teal-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-4">Verifying Payment...</h1>
              <p className="text-gray-600">Please wait while we confirm your order</p>
            </div>
          )}
          
          {status === 'success' && (
            <div data-testid="payment-success">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-4xl font-bold mb-4 text-green-600">Payment Successful!</h1>
              <p className="text-gray-600 mb-8">Thank you for your purchase. Your order has been confirmed.</p>
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/shop')}
                  className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 rounded-full"
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div data-testid="payment-error">
              <h1 className="text-3xl font-bold mb-4 text-red-600">Payment Verification Failed</h1>
              <p className="text-gray-600 mb-8">We couldn't verify your payment. Please contact support.</p>
              <Button 
                onClick={() => navigate('/cart')}
                className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 rounded-full"
              >
                Return to Cart
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;